import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { sumExpr, keepVisible, FIELD } from "./imageUtils";

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs font-medium text-slate-600">{label}</span>{children}</label>
);

// Inline (non-popup) editor for one notebook line. Changes stay local until Save.
export const EntryEditor = ({ entry, plant, sessionId, customers, onDone, onCancel, defaults = {} }) => {
  const [f, setF] = useState({
    date: entry?.date || defaults.date || "", customer_read: entry?.customer_read ?? defaults.customer_read ?? "",
    tons_text: entry?.tons_text ?? "", matched_customer: entry?.matched_customer || defaults.matched_customer || "",
  });
  const [saving, setSaving] = useState(false);
  const tons = sumExpr(f.tons_text);
  const id = entry?.id || "new";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.date) return toast.error("Please choose the date");
    if (!f.customer_read.trim()) return toast.error("Please type the customer name");
    if (tons == null) return toast.error("Quantity must be like 4.5 or 3+4.5");
    setSaving(true);
    try {
      if (entry) {
        const body = { date: f.date, tons_text: f.tons_text, confirmed: true };
        if (f.customer_read !== entry.customer_read) body.customer_read = f.customer_read;
        body.matched_customer = f.matched_customer || "";
        if (!f.matched_customer && f.customer_read === entry.customer_read && entry.new_customer) body.new_customer = true;
        await api.patch(`/verification/entries/${entry.id}`, body);
      } else {
        await api.post(`/verification/sessions/${sessionId}/entries`, { plant, ...f, matched_customer: f.matched_customer || null });
      }
      toast.success("Saved");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={`entry-editor-${id}`} className="bg-sky-50 border-2 border-sky-300 rounded-xl p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Date"><Input type="date" data-testid={`editor-date-${id}`} className={FIELD} value={f.date} onChange={set("date")} onFocus={keepVisible} /></Field>
        <Field label="Customer name (as in notebook)">
          <Input type="text" data-testid={`editor-name-${id}`} className={FIELD} value={f.customer_read} onChange={set("customer_read")} onFocus={keepVisible}
            autoComplete="off" autoCorrect="off" autoCapitalize="words" spellCheck={false} enterKeyHint="next" />
        </Field>
        <Field label="Quantity in tons (use + for separate deliveries)">
          <Input type="text" inputMode="decimal" data-testid={`editor-tons-${id}`} className={FIELD} value={f.tons_text} placeholder="e.g. 4.5 or 3+4.5"
            onChange={(e) => setF({ ...f, tons_text: e.target.value.replace(/[^\d.+]/g, "") })} onFocus={keepVisible} autoComplete="off" enterKeyHint="done" />
          <span data-testid={`editor-total-${id}`} className={`text-xs ${tons == null ? "text-red-600" : "text-slate-600"}`}>{tons == null ? "Not a valid quantity" : `Total = ${tons} t`}</span>
        </Field>
        <Field label="Website customer">
          <select data-testid={`editor-match-${id}`} className="w-full h-11 rounded-md border border-slate-300 bg-white px-2 text-base" value={f.matched_customer} onChange={set("matched_customer")}>
            <option value="">Same as name / decide later</option>
            {customers.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" className="h-11 px-5" data-testid={`editor-cancel-${id}`} onClick={onCancel}>Cancel</Button>
        <Button type="button" className="h-11 px-6 bg-emerald-600 hover:bg-emerald-700" data-testid={`editor-save-${id}`} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
};
